import { JoyInteractableMap, MAP_CARDS_PORTAL_ID } from '@shared/components/JoyInteractableMap';
import { JoyInteractableMapProvider, useJoyInteractableMapContext } from '@shared/components/JoyInteractableMap/JoyInteractableMap.provider';
import { Box, ButtonV2, Spinner } from '@withjoy/joykit';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useGoogleInteractableMap } from '@shared/components/JoyInteractableMap/JoyInteractableMap.utils';
import { useMediaQuery } from '@withjoy/joykit/utils';
import { MapMarker } from './MapMarker';
import { ChevronLeftSquare, ChevronRightSquare, Search } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { TelemetryProvider, useMapTelemetry } from './Map.telemetry';
import { ListingWithPricing } from '@apps/accommodations/AccommodationsConsole.types';
import { milesToKm } from '@shared/utils/milesToMeters';
import React, { useEffect, useRef, useState } from 'react';
import { MapProps } from './Map.types';
import { LatLngLiteral } from '@shared/hooks/useGooglePlaceAPI';

const DEFAULT_RADIUS_KM = milesToKm(2); // ~3.2 km
const ZOOM_PAN_CONDITIONS = [
  { zoom: 18, distance: 100 },
  { zoom: 17, distance: 300 },
  { zoom: 16, distance: 500 },
  { zoom: 14, distance: 1000 },
  { zoom: 12, distance: 2000 },
  { zoom: 10, distance: 5000 }
];

const MapWithJoyInteractableMap = ({
  hotels,
  isFetchingHotels,
  location,
  onChangeLocation,
  onChangeMapViewPort,
  onClickMarker,
  hidePanel,
  onToggleLeftPanel,
  forceUpdateMap,
  setForceUpdateMap
}: MapProps) => {
  const { map } = useJoyInteractableMapContext();
  const { reduceBounds, getRadiusFromBounds } = useGoogleInteractableMap();
  const telemetry = useMapTelemetry();
  const isMobile = useMediaQuery(theme => theme.mediaQueries.between(0, { breakpointAlias: 'sm2' }));

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const centerChangeCount = useRef(0);
  const zoomChangeCount = useRef(0);
  const prevZoom = useRef<number | undefined>();
  const debounceTimeout = useRef<ReturnType<typeof setTimeout>>();
  const [isMapInitialized, setIsMapInitialized] = useState(false);
  const [initialZoom, setInitialZoom] = useState<number | undefined>();

  const [initialMapCenter, setInitialMapCenter] = useState<LatLngLiteral | null>(null);
  const [activeMarker, setActiveMarker] = useState<string>();
  const [pendingCenterChange, setPendingCenterChange] = useState<{ lat: number; lng: number; bounds?: google.maps.LatLngBounds }>();

  const calculateChangedLocation = useEventCallback((lat: number, lng: number, bounds?: google.maps.LatLngBounds) => {
    const reduced = bounds && reduceBounds ? reduceBounds(bounds, 0.1) : null;
    if (reduced && getRadiusFromBounds) {
      const { radius, center } = getRadiusFromBounds(reduced);
      return { location: { lat: center.lat(), lng: center.lng() }, radius: +radius };
    }
    return { location: { lat, lng }, radius: +DEFAULT_RADIUS_KM.toFixed(0) };
  });

  const handleZoomChanged = useEventCallback(() => {
    if (!map) return;
    if (zoomChangeCount.current === 0) {
      setInitialZoom(map.getZoom());
    }
    if (zoomChangeCount.current++ === 0) return;

    telemetry.mapInteraction('zoom');

    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      const bounds = map.getBounds();
      if (bounds) onChangeMapViewPort?.(bounds);
    }, 200);
  });

  const handleCenterChanged = useEventCallback((lat: number, lng: number, distanceToPrevCenter: number) => {
    const bounds = map?.getBounds();
    const changedLocation = calculateChangedLocation(lat, lng, bounds);

    if (changedLocation) {
      onChangeMapViewPort?.(bounds);
    }

    if (centerChangeCount.current++ === 0) return;

    telemetry.mapInteraction('pan');
    const zoom = map?.getZoom();

    if (zoom && (ZOOM_PAN_CONDITIONS.some(c => zoom >= c.zoom && distanceToPrevCenter >= c.distance) || zoom !== prevZoom.current)) {
      setPendingCenterChange({ lat, lng, bounds });
      prevZoom.current = zoom;
    }
  });

  const handleSearchArea = useEventCallback(() => {
    if (pendingCenterChange) {
      const { lat, lng, bounds } = pendingCenterChange;
      const newLocation = calculateChangedLocation(lat, lng, bounds);
      onChangeLocation?.(newLocation);
      setPendingCenterChange(undefined);
    }
  });

  const handleMarkerClick = useEventCallback((hotel: ListingWithPricing) => {
    setActiveMarker(prev => (prev === hotel.joyPlaceId ? undefined : hotel.joyPlaceId));
    onClickMarker?.(hotel);
  });

  const handleMapClick = useEventCallback((e: MouseEvent) => {
    if (!mapContainerRef.current?.contains(e.target as Node)) {
      e.stopPropagation();
      setActiveMarker(undefined);
    }
  });

  useEffect(() => {
    if (!isMobile) {
      document.addEventListener('click', handleMapClick);
      return () => document.removeEventListener('click', handleMapClick);
    }
    return () => {};
  }, [handleMapClick, isMobile]);

  // Set initial center
  useEffect(() => {
    if (!initialMapCenter && location?.latlng) {
      setInitialMapCenter(location.latlng);
    }
  }, [location, initialMapCenter, setInitialMapCenter]);

  useEffect(() => {
    if (forceUpdateMap && location?.latlng && initialZoom) {
      map?.panTo(location.latlng);
      map?.setZoom(initialZoom);
      setForceUpdateMap?.(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceUpdateMap, location, map, setForceUpdateMap, initialZoom]);

  // Initialize map on first bounds
  useEffect(() => {
    if (!isMapInitialized && map?.getBounds() && getRadiusFromBounds) {
      const bounds = map.getBounds();
      setIsMapInitialized(true);
      onChangeMapViewPort?.(bounds);
    }
  }, [map, isMapInitialized, getRadiusFromBounds, onChangeMapViewPort]);

  if (!initialMapCenter) return null;

  return (
    <Box width="100%" ref={mapContainerRef} flex={1} position="relative" borderTopRightRadius="inherit" borderBottomRightRadius="inherit">
      <JoyInteractableMap
        center={initialMapCenter}
        autoCenterAndZoom
        onClick={handleMapClick}
        radiusMiles={2}
        gestureHandling="greedy"
        onZoomChanged={handleZoomChanged}
        onCenterChanged={handleCenterChanged}
      >
        {isFetchingHotels && (
          <Box position="absolute" left="50%" top={9} transform="translate(-50%, -50%)">
            <Spinner />
          </Box>
        )}
        {pendingCenterChange && (
          <ButtonV2 position="absolute" left="50%" top={7} transform="translate(-50%, 0)" onClick={handleSearchArea} startIcon={<Search />} intent="newPrimary" shape="rounded">
            Search this area
          </ButtonV2>
        )}
        <ButtonV2
          onClick={onToggleLeftPanel}
          startIcon={hidePanel ? <ChevronRightSquare size={24} /> : <ChevronLeftSquare size={24} />}
          width={pxToRem(44)}
          height={pxToRem(44)}
          backgroundColor="white"
          color="mono14"
          _hover={{ color: 'white' }}
          _active={{ color: 'white' }}
          intent="newPrimary"
          top={7}
          left={0}
          borderRadius={0}
          borderTopRightRadius={3}
          borderBottomRightRadius={3}
        />
        {isMobile && <Box id={MAP_CARDS_PORTAL_ID} position="fixed" width="100%" padding={5} bottom={0} left={0} />}
        {hotels?.map(hotel => (
          <MapMarker
            key={hotel.joyPlaceId}
            hotel={hotel}
            isHighlighted={activeMarker === hotel.joyPlaceId}
            activeMarker={activeMarker}
            isHoveringAddedHotel={false}
            onClickMarker={handleMarkerClick}
          />
        ))}
      </JoyInteractableMap>
    </Box>
  );
};

export const Map = (props: MapProps) => (
  <JoyInteractableMapProvider>
    <TelemetryProvider>
      <MapWithJoyInteractableMap {...props} />
    </TelemetryProvider>
  </JoyInteractableMapProvider>
);
